import { Breadcrumb, Col, Row, DatePicker, Select, message } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMutation } from "react-query";
import Title from "../UI/Title/Title";
import AddButton from "../UI/AddButton/addbutton";
import ResetButton from "../UI/ResetButton/resetbutton";
import { Search } from "../UI/Search/Search";
import CountList from "./CountList";
import AddCount from "./AddCount";
import { CountInterface, WarehouseInterface } from "./Interfaces";
import { getCounts } from "../../api/inventoryCount";
import { getWarehouses } from "../../api/warehouse";
import moment from "moment";

const Count = () => {
  const { RangePicker } = DatePicker;
  const { t } = useTranslation("common");
  const [searchText, setSearchText] = useState<string>("");
  const [tableKey, setTableKey] = useState<number>(0);
  const [pickerDate, setPickerDate] = useState({ startDate: '', endDate: '' });
  const [counts, setCounts] = useState<CountInterface[]>([])
  const [countsSearch, setCountsSearch] = useState<CountInterface[]>([])
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [warehouses, setWarehouses] = useState<WarehouseInterface[]>([])
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null)

  const { mutateAsync: getCountsMutation, isLoading } = useMutation(
    "getCounts",
    ({ warehouseId }: { warehouseId: number | null }) =>
      getCounts({ warehouseId: warehouseId }),
    {
      onSuccess: ({ data }) => {
        setCounts(data.data)
        setCountsSearch(data.data)
      },
    }
  );

  const { mutateAsync: getWarehousesMutation, isLoading: warehousesLoading } = useMutation(
    "getWarehouses",
    () => {
      return getWarehouses()
    },
    {
      onSuccess: ({ data }) => {
        setWarehouses(data)
        setSelectedWarehouseId(data[0].id)
      },
      onError: ({ response }) => {
        message.destroy()
        message.error(response.message ? response.message : "failed to load warehouses");
      },
    }
  );

  useEffect(() => {
    getWarehousesMutation()
    getCountsMutation({ warehouseId: 1 })
  }, [])

  useEffect(() => {
    handleSearch()
  }, [pickerDate, searchText])

  const handleSearch = () => {
    const filteredData = countsSearch.length > 0 ? countsSearch : counts;
    const filteredList = filteredData.filter(item => {
      let nameMatch = item.name.toLowerCase().includes(searchText.trim().toLowerCase());
      const dateMatch =
        (!pickerDate.startDate || pickerDate.startDate < moment(item.date).format("MM/DD/YYYY")) &&
        (!pickerDate.endDate || pickerDate.endDate > moment(item.date).format("MM/DD/YYYY"));
      return nameMatch && dateMatch;
    });
    setCounts(filteredList);
  };

  const handleDatePicker = (dates: any, stringDates: any) => {
    const currTime = moment().format('HH:mm:ss')
    setPickerDate({
      startDate: stringDates[0] + currTime,
      endDate: stringDates[1] + currTime
    })
  }

  const handleResetFilters = () => {
    if (!pickerDate.startDate && !pickerDate.endDate && !searchText) return
    setTableKey((tableKey) => tableKey + 1);
    setSearchText("");
    setPickerDate({
      startDate: '',
      endDate: ''
    })
  };

  const handleAddList = () => {
    setIsAddItemOpen(true)
  }

  const handleWarehouseId = (value: number) => {
    setSelectedWarehouseId(value)
    getCountsMutation({ warehouseId: value })
  };

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col offset={1} span={22} style={{
          marginBottom: "10px",
        }}>
          <Breadcrumb
            items={[
              {
                title: <Link to="/main">{t("home")}</Link>,
              },
              {
                title: <>{t("inventory")}</>,
              },
              {
                title: (
                  <span className="breadcrumbcolor">{t("count")}</span>
                ),
              },
            ]}
          />
        </Col>
        <Col offset={1} span={22}>
          <Title title={`${t("count")}`} />
        </Col>
        <Col offset={1} span={4}>
          <Search
            value={searchText}
            onChange={(e: any) => setSearchText(e.target.value)}
          />
        </Col>
        <Col span={4}>
          <RangePicker
            size="large"
            key={tableKey}
            format={"MM/DD/YYYY"}
            onChange={(dates, stringDates) => {
              handleDatePicker(dates, stringDates)
            }}
            placeholder={[t('start-date'), t('end-date')]}
          />
        </Col>
        <Col span={2}>
          <Select
            style={{ width: "110%" }}
            size="large"
            placeholder={t("warehouse")}
            loading={warehousesLoading}
            onChange={(value) => handleWarehouseId(value)}
            value={selectedWarehouseId}
          >
            {warehouses.map((warehouse) => (
              <Select.Option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col
          style={{ display: "flex", justifyContent: "flex-end", gap: "20px" }}
          offset={10}
          span={2}
        >
          <Col>
            <ResetButton
              onClick={() => {
                handleResetFilters();
              }}
            />
          </Col>
          <AddButton onClick={() => handleAddList()} />
        </Col>
        <Col offset={1} span={22}>
          <CountList tableKey={tableKey}
            counts={counts}
            countsSearch={countsSearch}
            setCounts={setCounts}
            isLoadingProp={isLoading}
            setCountsSearch={setCountsSearch}
          />
        </Col>
      </Row>
      <AddCount
        isAddItemOpen={isAddItemOpen}
        setIsAddItemOpen={setIsAddItemOpen}
        setCounts={setCounts}
        setCountsSearch={setCountsSearch}
        warehouses={warehouses}
        warehousesLoading={warehousesLoading}
        selectedWarehouseId={selectedWarehouseId}
      />
    </>
  );
};

export default Count;
